import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography
} from "@mui/material";
import SortIcon from '@mui/icons-material/Sort';
import { enqueueSnackbar } from "notistack";
import * as XLSX from 'xlsx';
import { FileDownload } from "@mui/icons-material";
import { useState } from "react";
import { Job } from "../types";

interface JobCountAndSortSectionProps {
    jobs: Job[];
    jobsCount: number;
    sortOrder: string;
    setSortOrder: (order: string) => void;
    sortBy: string;
    setSortBy: (by: string) => void;
}

export const JobCountAndSortSection = ({
    jobs,
    jobsCount,
    sortOrder,
    setSortOrder,
    sortBy,
    setSortBy
}: JobCountAndSortSectionProps) => {
    const [loading, setLoading] = useState(false);

    const exportToExcel = () => {
        try {
            setLoading(true);
            // Create a new workbook
            const workbook = XLSX.utils.book_new();

            // Prepare data for export including ALL fields
            const exportData = jobs.map(job => ({
                'Title': job.title || '-',
                'Description': job.description || '-',
                'Company': job.company || '-',
                'Location': job.location || '-',
                'Category': job.category || '-',
                'Type': job.type || '-',
                'Salary': job.salary || '-',
                'From Crawler': job.fromCrawlerSearch ? job.fromCrawlerSearch + ' - ' + job.fromCrawlerProvider : '-',
                'External URL': job.externalUrl || '-',
                'Created At': job.createdAt || '-'
            }));

            // Convert data to worksheet
            const worksheet = XLSX.utils.json_to_sheet(exportData);

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Jobs');

            // Generate and download Excel file
            XLSX.writeFile(workbook, 'jobs_list.xlsx');

            enqueueSnackbar('Jobs exported successfully', { variant: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            enqueueSnackbar('Failed to export jobs', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSortByChange = (event: SelectChangeEvent<string>) => {
        setSortBy(event.target.value);

        switch (event.target.value) {
            case "newest":
                setSortOrder("desc");
                setSortBy("createdAt");
                break;
            case "oldest":
                setSortOrder("asc");
                setSortBy("createdAt");
                break;
        }
    };

    const getSortByValue = (sortBy: string, sortOrder: string) => {
        switch (sortBy) {
            case "createdAt":
                return sortOrder === "desc" ? "newest" : "oldest";
            default:
                return sortOrder === "desc" ? "newest" : "oldest";
        }
    };

    return (
        <Stack gap={2} py={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">
                    {jobsCount} jobs
                </Typography>

                <Stack direction="row" gap={2} alignItems="center">
                    <SortIcon sx={{ mr: 1, color: "text.secondary" }} />
                    <FormControl size="small" sx={{ minWidth: 150, bgcolor: "background.paper" }}>
                        <InputLabel>Sort by</InputLabel>
                        <Select
                            value={getSortByValue(sortBy, sortOrder)}
                            label="Sort by"
                            onChange={handleSortByChange}
                        >
                            <MenuItem value="newest">Newest first</MenuItem>
                            <MenuItem value="oldest">Oldest first</MenuItem>
                        </Select>
                    </FormControl>

                    <Stack alignItems="flex-end">
                        <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={exportToExcel}
                            disabled={loading || jobs.length === 0}
                        >
                            Export to Excel
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Stack >
    )
}